import { Box, Stack, Typography, useTheme } from '@mui/material';
import type { FC } from 'react';
import { IconsMap } from '~/constants/iconsMap';
import { isKeyof } from '~/types';
import type { FeaturedIconPointsProps } from '~/types/sanity/components';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';

export type FeaturedIconPointProps = FeaturedIconPointsProps['points'][0];

const labelPositionMap: Record<FeaturedIconPointProps['labelPosition'], string> = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
};

export const FeaturedIconPoint: FC<FeaturedIconPointProps> = ({
  _key,
  icon: { name: iconName },
  backgroundColor,
  title,
  labelPosition,
}) => {
  const theme = useTheme();
  const Icon = isKeyof(IconsMap, iconName) ? IconsMap[iconName] : null;
  const getCompoundTestId = getCompoundTestIdGenerator('featuredIconPoint');
  return (
    <Stack
      direction={{ xs: 'row' }}
      spacing={{ xs: 2, md: 3 }}
      alignItems={labelPositionMap[labelPosition]}
      data-testid={getCompoundTestId(`item-${_key}`)}
    >
      {Icon && (
        <Box
          display="inline-flex"
          justifyContent="center"
          alignItems="center"
          width={{ xs: theme.spacing(4.25), sm: theme.spacing(5.25), md: theme.spacing(6) }}
          height={{ xs: theme.spacing(4.25), sm: theme.spacing(5.25), md: theme.spacing(6) }}
          bgcolor={backgroundColor}
          borderRadius={{ xs: 2, sm: 3 }}
          data-testid={getCompoundTestId('icon')}
        >
          <Icon style={{ width: theme.spacing(2.25), height: theme.spacing(2.25) }} />
        </Box>
      )}

      <Box flex={1}>
        <Typography
          variant="body2"
          fontSize={{ sm: theme.typography.body1.fontSize }}
          lineHeight={{ sm: theme.typography.body1.lineHeight }}
          data-testid={getCompoundTestId('title')}
        >
          {title}
        </Typography>
      </Box>
    </Stack>
  );
};
